import { useQueries } from "@tanstack/react-query";
import { metricsApi } from "../api/metrics";
import { qk } from "../../lib/queryKeys";
import type { MetricsFilters, MetricsComparisonParams } from "../api/types";
import { toComparisonKey, getMetricRagStatus } from '../utils';
import type { RagColor } from '../utils';

/** Demographic dimensions a metric can be broken down by (region not yet supported by the API). */
export const BREAKDOWN_DIMENSIONS = [
  { groupBy: "department", label: "By department" },
  { groupBy: "age_group", label: "By age" },
  { groupBy: "gender", label: "By gender" },
  { groupBy: "shift_pattern", label: "By shift pattern" },
] as const;

export const PROFILE_BREAKDOWN_DIMENSIONS = [
  { groupBy: "job_role", label: "By role" },
  { groupBy: "age_group", label: "By age" },
  { groupBy: "gender", label: "By gender" },
  { groupBy: "shift_pattern", label: "By shift pattern" },
] as const;

export type BreakdownGroupByValue =
  | (typeof BREAKDOWN_DIMENSIONS)[number]["groupBy"]
  | (typeof PROFILE_BREAKDOWN_DIMENSIONS)[number]["groupBy"];

export interface BreakdownRow {
  /** Stable identifier for the row — the comparison API's `group_key`. */
  groupKey: string;
  label: string;
  value: number;
  ragColor?: RagColor;
  ragLabel?: string;
  /** Department rows only: per-role breakdown within this department, revealed on expand. */
  children?: BreakdownRow[];
}

export interface MetricBreakdownDimension {
  groupBy: BreakdownGroupByValue;
  label: string;
  rows: BreakdownRow[];
  isLoading: boolean;
  isError: boolean;
}

export interface UseMetricBreakdownsResult {
  /** True when this metric maps to a comparison-API-supported key. */
  supported: boolean;
  dimensions: MetricBreakdownDimension[];
  isLoading: boolean;
}

/**
 * Fetches a metric's value broken down by each demographic dimension
 * (department / age / gender / shift), in parallel, and attaches RAG status to
 * each group using the UI metric's bands. Powers the inline metric drill-down
 * panels. Returns `supported: false` for metrics the comparison API can't break
 * down (e.g. steps, workout time) so callers can hide the affordance.
 *
 * `enabled` lets callers defer the requests until the panel is actually opened.
 */
export const useMetricBreakdowns = (
  uiMetricId: string,
  filterParams: MetricsFilters,
  options?: {
    enabled?: boolean;
    dimensions?: ReadonlyArray<{ groupBy: string; label: string }>;
  }
): UseMetricBreakdownsResult => {
  const comparisonKey = toComparisonKey(uiMetricId);
  const supported = comparisonKey !== null;
  const enabled = (options?.enabled ?? true) && supported;
  const dims = options?.dimensions ?? BREAKDOWN_DIMENSIONS;

  const results = useQueries({
    queries: dims.map((dim) => {
      const params: MetricsComparisonParams = {
        ...filterParams,
        group_by: dim.groupBy as BreakdownGroupByValue,
        metrics: comparisonKey ? [comparisonKey] : [],
      };
      return {
        queryKey: qk.metrics.comparison(params),
        queryFn: () => metricsApi.getComparison(params),
        staleTime: 5 * 60 * 1000,
        enabled,
      };
    }),
  });

  const dimensions: MetricBreakdownDimension[] = dims.map(
    (dim, index) => {
      const result = results[index];
      const groups = result?.data?.data?.groups ?? [];
      const toRow = (group: (typeof groups)[number]): BreakdownRow | null => {
        const raw = comparisonKey ? group.values[comparisonKey] : null;
        if (typeof raw !== "number") return null;
        const rounded = Math.round(raw * 10) / 10;
        // RAG bands live on the UI metric, not the comparison key.
        const status = getMetricRagStatus(uiMetricId, rounded);
        return {
          groupKey: group.group_key,
          label: group.group_label,
          value: rounded,
          ragColor: status.ragColor,
          ragLabel: status.label,
        };
      };

      let rows: BreakdownRow[];
      if (dim.groupBy === "department") {
        // The comparison API returns department-only "parent" rows (group_key
        // has no "::") alongside "Dept::Role" child rows for the same
        // department, so the department total and its role breakdown come
        // from one query. Nest children under their parent here.
        const parents: BreakdownRow[] = [];
        const childrenByDept = new Map<string, BreakdownRow[]>();
        groups.forEach((group) => {
          if (group.group_key === "unknown") return;
          const row = toRow(group);
          if (!row) return;
          const sep = group.group_key.indexOf("::");
          if (sep === -1) {
            parents.push(row);
          } else {
            const deptKey = group.group_key.slice(0, sep);
            const siblings = childrenByDept.get(deptKey) ?? [];
            siblings.push(row);
            childrenByDept.set(deptKey, siblings);
          }
        });
        rows = parents
          .map((parent) => ({
            ...parent,
            children: childrenByDept
              .get(parent.groupKey)
              ?.sort((a, b) => a.label.localeCompare(b.label)),
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
      } else {
        rows = groups.flatMap((group) => {
          if (group.group_key === "unknown") return [];
          const row = toRow(group);
          return row ? [row] : [];
        });
      }

      return {
        groupBy: dim.groupBy as BreakdownGroupByValue,
        label: dim.label,
        rows,
        isLoading: result?.isLoading ?? false,
        isError: result?.isError ?? false,
      };
    }
  );

  return {
    supported,
    dimensions,
    isLoading: enabled && results.some((r) => r.isLoading),
  };
};
